/**
 * Page: Login (Journey-Based)
 *
 * Page d'entrée pour tous les types de login :
 * - Email OTP
 * - SMS OTP
 * - Password
 * - OIDC (Google/Apple)
 *
 * Cette page peut intervenir dans plusieurs journeys :
 * - LOGIN_EMAIL_OTP (état: email_input)
 * - LOGIN_SMS_OTP (état: phone_input)
 * - LOGIN_PASSWORD (état: password_login)
 * - LOGIN_OIDC (états: oidc_initiation, oidc_callback, account_linking)
 */

import { initPhoneInputs } from '@/_includes/components/phone-input.js';
import { authContext } from '@/scripts/utils/auth-context.js';
import { alert } from '@/scripts/utils/alert-message.js';
import { getDefaultMethodForPlatform } from '@/scripts/platform-detection.js';
import { KratosFlows } from '@/scripts/api/ory-client.js';

document.addEventListener('DOMContentLoaded', async () => {
  // ============================================
  // INITIALIZATION
  // ============================================
  await authContext.init();

  // Vérifier si un journey est en cours
  const currentJourney = authContext.getCurrentJourney();
  const currentState = authContext.getCurrentState();

  // ============================================
  // VÉRIFICATIONS DE CONNEXION
  // ============================================
  // ✅ Si aucun journey en cours + déjà connecté → vérifier OAuth puis rediriger
  if (!currentJourney && authContext.isAuthenticated()) {
    // Si OAuth challenge présent, rediriger vers oauth-callback au lieu de settings
    if (authContext.has('oauthLoginChallenge')) {
      console.log('[Login] User already authenticated with OAuth challenge, redirecting to oauth-callback');
      window.location.href = '/oauth-callback.html';
    } else {
      window.location.href = '/settings.html';
    }
    return;
  }

  // ✅ Si journey LOGIN en cours + déjà connecté → clear journey + rediriger
  if (currentJourney && currentJourney.name.startsWith('LOGIN') && authContext.isAuthenticated()) {
    // Sauvegarder OAuth challenge AVANT de clear (car clearJourney efface tout)
    const hasOAuthChallenge = authContext.has('oauthLoginChallenge');
    const oauthChallenge = hasOAuthChallenge ? authContext.get('oauthLoginChallenge') : null;

    authContext.clearJourney();

    // Remettre le challenge si présent
    if (hasOAuthChallenge) {
      authContext.save('oauthLoginChallenge', oauthChallenge);
      console.log('[Login] User already authenticated with OAuth challenge, redirecting to oauth-callback');
      window.location.href = '/oauth-callback.html';
    } else {
      alert.success('#alert-container', 'Vous êtes déjà connecté. Redirection vers vos paramètres...', {
        icon: true,
        closable: false,
        duration: 2000
      });
      setTimeout(() => {
        window.location.href = '/settings.html';
      }, 2000);
    }
    return;
  }

  // ============================================
  // JOURNEY VALIDATION (Entry Point Pattern)
  // ============================================
  // Si journey en cours mais pas de type LOGIN → clear + rester
  if (currentJourney && !currentJourney.name.startsWith('LOGIN')) {
    authContext.clearJourney();
    // NOTE: Message commenté car souvent confus pour l'utilisateur
    // alert.info('#alert-container', 'Vous pouvez commencer une nouvelle connexion.', {
    //   icon: true,
    //   closable: true
    // });
  }

  // ============================================
  // UI SETUP (AVANT ACCOUNT LINKING)
  // ============================================
  const tabsContainer = document.querySelector('[data-login-tabs]');
  const tabButtons = document.querySelectorAll('[data-login-tabs] [data-tab]');
  const tabContents = document.querySelectorAll('[data-tab-content]');
  const forms = document.querySelectorAll('form[data-login-method]');

  if (!tabsContainer || tabButtons.length === 0) return;

  // Initialiser phone input
  initPhoneInputs();

  // Déterminer la méthode par défaut selon la plateforme
  const defaultMethod = getDefaultMethodForPlatform();

  /**
   * Active un tab spécifique
   */
  function activateTab(tabName) {
    tabButtons.forEach((btn) => {
      const isActive = btn.dataset.tab === tabName;
      btn.setAttribute('aria-selected', isActive);
      btn.classList.toggle('border-primary-600', isActive);
      btn.classList.toggle('text-primary-600', isActive);
      btn.classList.toggle('border-transparent', !isActive);
      btn.classList.toggle('text-gray-500', !isActive);
    });

    tabContents.forEach((content) => {
      const shouldShow = content.dataset.tabContent === tabName;
      content.classList.toggle('hidden', !shouldShow);
    });
  }

  // ============================================
  // LIAISON DE COMPTE (Account Linking)
  // ============================================
  // Note: Le journey LOGIN_OIDC avec state account_linking peut rediriger vers cette page
  // depuis oidc-callback.html quand un compte existe déjà avec l'email Google
  if (currentJourney?.name === 'LOGIN_OIDC' && currentState?.name === 'account_linking') {
    console.log('[Login] Account linking required');

    const email = authContext.get('linkingEmail');
    const methods = authContext.get('linkingMethods');

    // L'action display_linking_message a déjà été exécutée dans oidc-callback.html
    // On affiche juste le message ici
    let message = 'Un compte existe déjà avec cet email. ';
    if (methods?.password && methods?.code) {
      message += 'Connectez-vous avec votre mot de passe ou demandez un code par email pour lier votre compte Google.';
    } else if (methods?.password) {
      message += 'Connectez-vous avec votre mot de passe pour lier votre compte Google.';
    } else if (methods?.code) {
      message += 'Demandez un code par email pour lier votre compte Google.';
    }

    alert.info('#alert-container', message, {
      icon: true,
      closable: true,
      duration: 0
    });

    // Pré-remplir l'email
    if (email) {
      const passwordEmailInput = document.querySelector('form[data-login-method="password"] [name="email"]');
      if (passwordEmailInput && methods?.password) {
        passwordEmailInput.value = email;
      }

      const codeEmailInput = document.querySelector('form[data-login-method="email"] [name="email"]');
      if (codeEmailInput && methods?.code) {
        codeEmailInput.value = email;
      }
    }

    // Activer le tab approprié
    if (methods?.password) {
      activateTab('password');
    } else if (methods?.code) {
      activateTab('email');
    }
  } else {
    // Activer le tab par défaut
    activateTab(defaultMethod);
  }

  // Gestion des clics sur les tabs
  tabButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const tabName = button.dataset.tab;
      activateTab(tabName);

      const newUrl = new URL(window.location);
      newUrl.searchParams.set('method', tabName);
      window.history.replaceState({}, '', newUrl);
    });
  });

  // ============================================
  // FORM SUBMISSIONS
  // ============================================

  // Form Email OTP
  const emailForm = document.querySelector('form[data-login-method="email"]');
  if (emailForm) {
    emailForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitButton = emailForm.querySelector('button[type="submit"]');

      submitButton.disabled = true;
      submitButton.textContent = 'Chargement...';

      try {
        const email = emailForm.querySelector('[name="email"]').value;

        // Vérifier si on est en mode account linking
        const isAccountLinking = authContext.get('accountLinkingRequired');
        const linkingFlowId = authContext.get('linkingFlowId');

        if (isAccountLinking && linkingFlowId) {
          // Mode account linking : utiliser le flow OIDC existant
          console.log('[Login] Account linking mode (email), using existing flow:', linkingFlowId);

          // Récupérer le flow OIDC
          const flow = await KratosFlows.getFlow(linkingFlowId, 'login');
          const csrfToken = flow.ui.nodes.find(n => n.attributes.name === 'csrf_token')?.attributes.value;

          // Soumettre avec code pour demander un OTP qui permettra de lier les comptes
          try {
            await KratosFlows.submitFlow(linkingFlowId, 'login', {
              method: 'code',
              identifier: email,
              csrf_token: csrfToken,
            });
          } catch (submitError) {
            // Status 400 avec state=sent_email = code envoyé avec succès
            const responseData = submitError.response?.data;
            const isCodeSent = submitError.response?.status === 400 &&
              (responseData?.ui?.messages?.some(m => m.type === 'info') || responseData?.state === 'sent_email');

            if (!isCodeSent) {
              throw submitError;
            }

            // Code envoyé : rester en mode linking et rediriger vers verification
            authContext.save('flow', linkingFlowId);
            authContext.save('csrfToken', csrfToken);
            authContext.save('identifier', email);
            authContext.save('method', 'email');

            window.location.href = '/verification-login.html';
            return;
          }
        } else {
          // Mode login normal
          authContext.startJourney('LOGIN_EMAIL_OTP', 'email_input');

          authContext.save('identifier', email);
          authContext.save('method', 'email');

          await authContext.executeCurrentState();
        }

      } catch (error) {
        console.error('[Login] Error in email OTP flow:', error);
        submitButton.disabled = false;
        submitButton.textContent = 'Continuer';
      }
    });
  }

  // Form SMS OTP
  const smsForm = document.querySelector('form[data-login-method="sms"]');
  if (smsForm) {
    smsForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitButton = smsForm.querySelector('button[type="submit"]');

      submitButton.disabled = true;
      submitButton.textContent = 'Chargement...';

      try {
        const phone = smsForm.querySelector('[name="phone"]').value;

        authContext.startJourney('LOGIN_SMS_OTP', 'phone_input');

        authContext.save('identifier', phone);
        authContext.save('method', 'sms');

        await authContext.executeCurrentState();

      } catch (error) {
        console.error('[Login] Error in SMS OTP flow:', error);
        submitButton.disabled = false;
        submitButton.textContent = 'Continuer';
      }
    });
  }

  // Form Password
  const passwordForm = document.querySelector('form[data-login-method="password"]');
  if (passwordForm) {
    passwordForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitButton = passwordForm.querySelector('button[type="submit"]');

      submitButton.disabled = true;
      submitButton.textContent = 'Chargement...';

      try {
        const email = passwordForm.querySelector('[name="email"]').value;
        const password = passwordForm.querySelector('[name="password"]').value;

        // Vérifier si on est en mode account linking
        const isAccountLinking = authContext.get('accountLinkingRequired');
        const linkingFlowId = authContext.get('linkingFlowId');

        if (isAccountLinking && linkingFlowId) {
          // Mode account linking : utiliser le flow OIDC existant
          console.log('[Login] Account linking mode, using existing flow:', linkingFlowId);

          // Récupérer le flow OIDC
          const flow = await KratosFlows.getFlow(linkingFlowId, 'login');
          const csrfToken = flow.ui.nodes.find(n => n.attributes.name === 'csrf_token')?.attributes.value;

          // Soumettre avec password pour lier les comptes
          const result = await KratosFlows.submitFlow(linkingFlowId, 'login', {
            method: 'password',
            identifier: email,
            password: password,
            csrf_token: csrfToken,
          });

          // Vérifier si session créée
          if (result.session) {
            authContext.save('session', result.session);

            // Vérifier OAuth challenge avant de clear
            const hasOAuthChallenge = authContext.has('oauthLoginChallenge');

            if (hasOAuthChallenge) {
              // OAuth flow → rediriger vers oauth-callback
              console.log('[Login] Account linking successful with OAuth challenge, redirecting to oauth-callback');
              window.location.href = '/oauth-callback.html';
            } else {
              // Pas d'OAuth → nettoyer et aller à settings
              authContext.clearJourney();
              alert.success('#alert-container', 'Compte Google lié avec succès !', {
                icon: true,
                closable: false,
                duration: 2000
              });
              setTimeout(() => {
                window.location.href = '/settings.html';
              }, 2000);
            }
          }
        } else {
          // Mode login normal
          // Démarrer le journey D'ABORD (nettoie le storage)
          authContext.startJourney('LOGIN_PASSWORD', 'password_login');

          // PUIS sauvegarder les données
          authContext.save('identifier', email);
          authContext.save('password', password);
          authContext.save('method', 'password');

          await authContext.executeCurrentState();
        }

      } catch (error) {
        console.error('[Login] Error in password flow:', error);
        submitButton.disabled = false;
        submitButton.textContent = 'Se connecter';
      }
    });
  }

  // ============================================
  // SOCIAL LOGIN BUTTONS (OIDC)
  // ============================================
  const socialButtons = document.querySelectorAll('[data-social-provider]');
  socialButtons.forEach((button) => {
    button.addEventListener('click', async () => {
      const provider = button.dataset.socialProvider;
      button.disabled = true;
      const originalHTML = button.innerHTML;
      button.innerHTML = '<span>Chargement...</span>';

      try {
        authContext.startJourney('LOGIN_OIDC', 'oidc_initiation');

        authContext.save('oidcProvider', provider);
        authContext.save('method', 'oidc');

        await authContext.executeCurrentState();

      } catch (error) {
        console.error('[Login] Error in OIDC flow:', error);
        button.disabled = false;
        button.innerHTML = originalHTML;
      }
    });
  });
});
